import React, { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useLang } from '../i18n';
import { publicApi, resolveImage } from '../api';
import { useFetch } from '../useFetch';

export default function CategoriesGrid() {
  const loc = useLocation();
  const slug = loc.pathname.split('/').filter(Boolean)[0];
  const { lang, t } = useLang();
  const nav = useNavigate();

  const { data: city } = useFetch(`city:${slug}`, () => publicApi.city(slug), [slug]);
  const { data: categories, loading: lc } = useFetch('categories', publicApi.categories);
  const { data: activities, loading: la } = useFetch(`acts:${slug}`, () => publicApi.activities({ city: slug }), [slug]);

  const catCounts = useMemo(() => {
    const m = {};
    (activities || []).forEach(a => { m[a.category_key] = (m[a.category_key] || 0) + 1; });
    return m;
  }, [activities]);

  if (!city || lc || la) return <div className="flex justify-center py-32"><Loader2 className="animate-spin text-gray-400" size={40} /></div>;

  const sortedCats = [...(categories || [])].sort((a, b) => (catCounts[b.key] || 0) - (catCounts[a.key] || 0) || a.order - b.order);

  return (
    <div>
      <section className="relative" style={{ minHeight: '38vh' }}>
        <div className="absolute inset-0">
          <img src={resolveImage(city.hero)} alt={city.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/50" />
        </div>
        <div className="relative z-10 flex flex-col items-center justify-center text-white text-center px-4 sm:px-6" style={{ minHeight: '38vh' }}>
          <p className="text-xs sm:text-sm tracking-widest uppercase opacity-90 fade-in-up">{city.full_name?.[lang] || city.name}</p>
          <h1 className="mt-2 text-2xl sm:text-3xl md:text-5xl font-bold tracking-wider fade-in-up fade-in-up-delay-1">{t('whatToSee')}</h1>
          <p className="mt-3 text-xs sm:text-sm text-white/80 tracking-wider fade-in-up fade-in-up-delay-2">POINTS OF INTEREST</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {sortedCats.map((cat, idx) => {
            const count = catCounts[cat.key] || 0;
            const Wrapper = count > 0 ? Link : 'div';
            return (
              <Wrapper
                key={cat.key}
                to={count > 0 ? `/${slug}/cosa-vedere/${cat.key}` : undefined}
                className={`category-tile fade-in-up ${count === 0 ? 'disabled' : ''}`}
                style={{ animationDelay: `${0.05 + idx * 0.04}s` }}
              >
                <img src={resolveImage(cat.image)} alt={cat.label?.[lang] || cat.label?.en || cat.key} loading="lazy" decoding="async" />
                <div className="category-overlay" />
                <div className="category-content">
                  <h3 className="category-title">{cat.label?.[lang] || cat.label?.en || cat.key}</h3>
                  <p className="category-desc">{cat.desc?.[lang] || cat.desc?.en || ''}</p>
                  {count > 0 ? (
                    <span className="count-badge mt-3">{count} {count === 1 ? 'attività' : 'attività'}</span>
                  ) : (
                    <span className="soon-badge mt-3">{t('soon')}</span>
                  )}
                </div>
              </Wrapper>
            );
          })}
        </div>
        <div className="mt-10 md:mt-14 text-center">
          <button onClick={() => nav(`/${slug}`)} className="inline-flex items-center gap-2 text-[color:var(--yup-primary)] font-semibold hover:underline">
            <ArrowLeft size={16} /> {city.full_name?.[lang] || city.name}
          </button>
        </div>
      </section>
    </div>
  );
}
